import { HyperFormula } from 'hyperformula';

enum HyperFormulaGridOperationsMethodName {
    SET_ROW_ORDER = 'setRowOrder',
    SET_COLUMN_ORDER = 'setColumnOrder',
}

type GridOperationsSuspensionState = {
    originalMethods: {
        [HyperFormulaGridOperationsMethodName.SET_ROW_ORDER]: HyperFormula['setRowOrder'];
        [HyperFormulaGridOperationsMethodName.SET_COLUMN_ORDER]: HyperFormula['setColumnOrder'];
    };
    suspendedSheetCounts: Map<number, number>;
};

type MNHyperFormulaEngine = HyperFormula & {
    __mnGridOperationsSuspensionState__?: GridOperationsSuspensionState;
};

const initialiseSuspension = (hfInstance: MNHyperFormulaEngine) => {
    const suspensionState: GridOperationsSuspensionState = {
        originalMethods: {
            [HyperFormulaGridOperationsMethodName.SET_ROW_ORDER]: hfInstance.setRowOrder.bind(hfInstance),
            [HyperFormulaGridOperationsMethodName.SET_COLUMN_ORDER]: hfInstance.setColumnOrder.bind(hfInstance),
        },
        suspendedSheetCounts: new Map<number, number>(),
    };

    Object.values(HyperFormulaGridOperationsMethodName).forEach((methodName) => {
        hfInstance[methodName] = (...args) => {
            const [sheetId] = args;

            const isSheetSyncStructureSuspended = (suspensionState.suspendedSheetCounts.get(sheetId) || 0) > 0;
            if (isSheetSyncStructureSuspended) return [];

            return suspensionState.originalMethods[methodName](...args);
        };
    });

    hfInstance.__mnGridOperationsSuspensionState__ = suspensionState;

    return suspensionState;
};

/**
 * Temporarily disables HyperFormula's grid operations for a sheet.
 */
export const suspendHyperFormulaGridOperationsForSheet = (
    engine: MNHyperFormulaEngine,
    sheetId: number,
): (() => void) => {
    const hfInstance = engine as MNHyperFormulaEngine;

    const suspensionState = hfInstance.__mnGridOperationsSuspensionState__ || initialiseSuspension(hfInstance);

    const currentSuspendedSheetCount = suspensionState.suspendedSheetCounts.get(sheetId) || 0;
    suspensionState.suspendedSheetCounts.set(sheetId, currentSuspendedSheetCount + 1);

    let isResumed = false;

    return () => {
        if (isResumed) return;
        isResumed = true;

        const suspensionCount = suspensionState.suspendedSheetCounts.get(sheetId) || 0;
        if (suspensionCount <= 1) {
            suspensionState.suspendedSheetCounts.delete(sheetId);
            return;
        }

        suspensionState.suspendedSheetCounts.set(sheetId, suspensionCount - 1);
    };
};
